import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Maximize2, Minimize2, AlertTriangle, Eye } from 'lucide-react';
import api from '../services/api';

const FilePreviewModal = ({ fichier, onClose, onDownload }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isPreviewable = (typeMime) => {
    if (!typeMime) return false;
    return (
      typeMime === 'application/pdf' ||
      typeMime.startsWith('image/') ||
      typeMime === 'text/plain'
    );
  };

  useEffect(() => {
    if (!fichier) return;

    let cancelled = false;
    let objectUrl = null;

    const loadPreview = async () => {
      setLoading(true);
      setError(null);
      setPreviewUrl(null);
      setTextContent(null);

      try {
        if (!isPreviewable(fichier.type_mime)) {
          setError('not_supported');
          setLoading(false);
          return;
        }

        const { blob } = await api.documents.getPreviewBlob(fichier.id);

        if (cancelled) return;

        if (fichier.type_mime === 'text/plain') {
          const text = await blob.text();
          if (!cancelled) setTextContent(text);
        } else {
          const namedBlob = new File([blob], fichier.nom_original, { type: blob.type });
          objectUrl = URL.createObjectURL(namedBlob);
          if (!cancelled) setPreviewUrl(objectUrl);
        }
      } catch (err) {
        console.error('Erreur preview:', err);
        if (!cancelled) setError('load_error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fichier]);

  // Fermer avec Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleBackdropClick = useCallback((e) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  if (!fichier) return null;

  const getFileIcon = (typeMime) => {
    if (typeMime?.includes('pdf')) return '📄';
    if (typeMime?.includes('word')) return '📝';
    if (typeMime?.includes('excel') || typeMime?.includes('spreadsheet')) return '📊';
    if (typeMime?.includes('image')) return '🖼️';
    if (typeMime === 'text/plain') return '📃';
    return '📁';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const renderPreviewContent = () => {
    // Loading
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <div
            className="w-12 h-12 border-4 rounded-full animate-spin mb-4"
            style={{ borderColor: '#88b9c5', borderTopColor: 'transparent' }}
          />
          <p className="text-sm font-medium text-gray-500">Chargement du document...</p>
        </div>
      );
    }

    // Erreur - type non supporté
    if (error === 'not_supported') {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <div className="text-6xl mb-6">{getFileIcon(fichier.type_mime)}</div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{fichier.nom_original}</h3>
          <p className="text-sm text-gray-500 mb-1">{formatFileSize(fichier.taille)}</p>
          <div className="mt-4 p-4 rounded-xl bg-amber-50 border border-amber-200 max-w-md">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={16} className="text-amber-600" />
              <span className="text-sm font-bold text-amber-700">Prévisualisation non disponible</span>
            </div>
            <p className="text-xs text-amber-600">
              Ce type de fichier ({fichier.type_mime}) ne peut pas être affiché directement. 
              Téléchargez-le pour l'ouvrir avec l'application appropriée.
            </p>
          </div>
          <button
            onClick={() => onDownload(fichier)}
            className="mt-6 flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-md hover:shadow-lg"
            style={{ backgroundColor: '#fed774', color: '#1f2937' }}
          >
            <Download size={16} />
            Télécharger le fichier
          </button>
        </div>
      );
    }

    // Erreur de chargement
    if (error === 'load_error') {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center px-8">
          <AlertTriangle size={48} className="text-red-400 mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">Erreur de chargement</h3>
          <p className="text-sm text-gray-500 mb-4">
            Impossible de charger ce document. Le fichier est peut-être introuvable sur le serveur.
          </p>
          <button
            onClick={() => onDownload(fichier)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-md hover:shadow-lg"
            style={{ backgroundColor: '#fed774', color: '#1f2937' }}
          >
            <Download size={16} />
            Essayer le téléchargement direct
          </button>
        </div>
      );
    }

    // PDF
    if (fichier.type_mime === 'application/pdf' && previewUrl) {
      return (
        <iframe
          src={previewUrl}
          className="w-full h-full border-0"
          title={fichier.nom_original}
        />
      );
    }

    // Images
    if (fichier.type_mime?.startsWith('image/') && previewUrl) {
      return (
        <div className="flex items-center justify-center h-full p-6 overflow-auto">
          <img
            src={previewUrl}
            alt={fichier.nom_original}
            className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
          />
        </div>
      );
    }

    // Texte
    if (fichier.type_mime === 'text/plain' && textContent !== null) {
      return (
        <div className="h-full overflow-auto p-6">
          <pre className="whitespace-pre-wrap font-mono text-sm text-gray-800 bg-white rounded-xl border border-gray-200 p-6 min-h-full">
            {textContent}
          </pre>
        </div>
      );
    }

    return null;
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000]"
      onClick={handleBackdropClick}
    >
      <div
        className={`bg-white flex flex-col transition-all duration-300 ${
          isFullscreen
            ? 'w-full h-full rounded-none'
            : 'w-[95vw] max-w-5xl h-[90vh] mx-4 rounded-2xl shadow-2xl'
        }`}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{
            background: 'linear-gradient(135deg, #88b9c5 0%, #6fa3b0 100%)',
            borderRadius: isFullscreen ? '0' : '1rem 1rem 0 0'
          }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl flex-shrink-0">{getFileIcon(fichier.type_mime)}</span>
            <div className="min-w-0">
              <h3 className="font-bold text-white text-sm truncate">{fichier.nom_original}</h3>
              <p className="text-xs text-white/70 font-medium">
                {formatFileSize(fichier.taille)}
                {fichier.dossier_nom && ` · ${fichier.dossier_nom}`}
                {fichier.uploaded_by_name && ` · par ${fichier.uploaded_by_name}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => onDownload(fichier)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:shadow-md"
              style={{ backgroundColor: '#fed774', color: '#1f2937' }}
            >
              <Download size={14} />
              Télécharger
            </button>
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
              title={isFullscreen ? 'Réduire' : 'Plein écran'}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex-1 overflow-hidden bg-gray-50" style={{ borderRadius: isFullscreen ? '0' : '0 0 1rem 1rem' }}>
          {renderPreviewContent()}
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;